import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LibraryBooks as LibraryBooksIcon,
  Edit as EditIcon,
  Upload as UploadIcon
} from '@mui/icons-material';
import { authGet, authPost, authDelete, authPut } from '../../services/apiService';
import DependencyTreeView from './DependencyTreeView';

const CommonLibrariesManagement = () => {
  const [libraries, setLibraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLibrary, setEditingLibrary] = useState(null);
  const [formData, setFormData] = useState({ library: '' });
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Fetch all common libraries
  const fetchLibraries = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await authGet('/api/common-library');
      
      if (response && response.success) {
        setLibraries(response.libraries || []);
      } else {
        throw new Error('Invalid response format');
      }
    } catch (err) {
      console.error('Error fetching common libraries:', err);
      setError(err.response?.data?.error || 'Failed to fetch common libraries');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLibraries();
  }, []);

  // Handle open add dialog
  const handleOpenAddDialog = () => {
    setEditingLibrary(null);
    setFormData({ library: '' });
    setDialogOpen(true);
  };

  // Handle open edit dialog
  const handleOpenEditDialog = (library) => {
    setEditingLibrary(library);
    setFormData({
      library: library.library
    });
    setDialogOpen(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingLibrary(null);
    setFormData({ library: '' });
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!formData.library.trim()) {
      setError('Library name is required');
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      if (editingLibrary) {
        // Update existing library
        await authPut(`/api/common-library/${editingLibrary._id}`, formData);
        setSuccess('Common library updated successfully');
      } else {
        // Create new library
        await authPost('/api/common-library', formData);
        setSuccess('Common library added successfully');
      }

      handleCloseDialog();
      fetchLibraries();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving common library:', err);
      setError(err.response?.data?.error || 'Failed to save common library');
    }
  };

  // Handle delete
  const handleDelete = async (libraryId) => {
    if (!window.confirm('Are you sure you want to delete this common library?')) {
      return;
    }

    try {
      setError(null);
      await authDelete(`/api/common-library/${libraryId}`);
      setSuccess('Common library deleted successfully');
      fetchLibraries();
      
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error deleting common library:', err);
      setError(err.response?.data?.error || 'Failed to delete common library');
    }
  };

  // Clear messages
  const clearMessages = () => {
    setError(null);
    setSuccess(null);
  };

  // Handle file selection for upload
  const handleFileSelect = (event) => {
    const file = event.target.files[0];
    if (file) {
      if (file.type !== 'application/json' && !file.name.toLowerCase().endsWith('.json')) {
        setError('Please select a JSON file');
        return;
      }
      setSelectedFile(file);
      setError(null);
    }
  };

  // Handle upload dependencies JSON
  const handleUploadDependencies = async () => {
    if (!selectedFile) {
      setError('Please select a JSON file');
      return;
    }

    try {
      setUploading(true);
      setError(null);
      setSuccess(null);

      const formData = new FormData();
      formData.append('dependenciesFile', selectedFile);

      // Use direct fetch for file upload (similar to uploadTsiTicketsCsv)
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('Authentication required');
      }

      const baseUrl = process.env.NODE_ENV === 'development' ? '' : window.RUNTIME_CONFIG?.API_URL || 'http://localhost:5001';
      const fullUrl = `${baseUrl}/api/common-library/upload-dependencies`;

      const response = await fetch(fullUrl, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `Upload failed with status: ${response.status}`);
      }

      const responseData = await response.json();
      if (responseData && responseData.success) {
        setSuccess(responseData.message || 'Dependencies uploaded successfully');
        setUploadDialogOpen(false);
        setSelectedFile(null);
        fetchLibraries();
        setTimeout(() => setSuccess(null), 5000);
      } else {
        throw new Error(responseData?.error || 'Upload failed');
      }
    } catch (err) {
      console.error('Error uploading dependencies:', err);
      setError(err.message || 'Failed to upload dependencies file');
    } finally {
      setUploading(false);
    }
  };


  if (loading && libraries.length === 0) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box p={3}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          <LibraryBooksIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
          Common Libraries Management
        </Typography>
          <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchLibraries}
            disabled={loading}
          >
            Refresh
          </Button>
          <Button
            variant="outlined"
            color="secondary"
            onClick={async () => {
              try {
                setError(null);
                setLoading(true);
                const response = await authPost('/api/common-library/seed');
                if (response && response.success) {
                  setSuccess(response.message || 'Common libraries seeded successfully');
                  fetchLibraries();
                  setTimeout(() => setSuccess(null), 5000);
                }
              } catch (err) {
                setError(err.response?.data?.error || 'Failed to seed libraries');
              } finally {
                setLoading(false);
              }
            }}
            disabled={loading}
          >
            Seed Libraries
          </Button>
          <Button
            variant="outlined"
            color="primary"
            startIcon={<UploadIcon />}
            onClick={() => setUploadDialogOpen(true)}
            disabled={loading}
          >
            Upload Dependencies JSON
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<AddIcon />}
            onClick={handleOpenAddDialog}
          >
            Add Library
          </Button>
        </Box>
      </Box>

      {/* Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={clearMessages}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={clearMessages}>
          {success}
        </Alert>
      )}

      {/* Libraries Table and Dependency Tree - Side by Side */}
      <Grid container spacing={2}>
        {/* Left Side - Libraries List */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Common Libraries List
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Libraries listed here will be identified as common-library issues in SNYK.
                Format: group:artifact (e.g., org.springframework.boot:spring-boot-dependencies).
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Library</strong></TableCell>
                      <TableCell><strong>Actions</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {libraries.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No common libraries found. Click "Add Library" to create one.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      libraries.map((library) => (
                        <TableRow key={library._id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="medium">
                              {library.library}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenEditDialog(library)}
                              sx={{ mr: 1 }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDelete(library._id)}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Right Side - Dependency Tree */}
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <React.Suspense fallback={<CircularProgress size={40} />}>
                <DependencyTreeView />
              </React.Suspense>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingLibrary ? 'Edit Common Library' : 'Add Common Library'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Library"
                value={formData.library}
                onChange={(e) => setFormData({ ...formData, library: e.target.value })}
                placeholder="e.g., org.springframework.boot:spring-boot-dependencies"
                helperText="Format: group:artifact"
                required
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSubmit} variant="contained" color="primary">
            {editingLibrary ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Upload Dependencies Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => !uploading && setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Dependencies JSON</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Upload a dependencies.json file to extract all dependencies under com.highq:common-dependencies.
            This will replace all existing common libraries with the extracted dependencies.
          </Typography>
          <input
            accept=".json,application/json"
            style={{ display: 'none' }}
            id="dependencies-file-upload"
            type="file"
            onChange={handleFileSelect}
          />
          <label htmlFor="dependencies-file-upload">
            <Button
              variant="outlined"
              component="span"
              fullWidth
              sx={{ mb: 2 }}
              disabled={uploading}
            >
              {selectedFile ? selectedFile.name : 'Select JSON File'}
            </Button>
          </label>
          {selectedFile && (
            <Typography variant="caption" color="text.secondary">
              Selected: {selectedFile.name} ({(selectedFile.size / 1024).toFixed(2)} KB)
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setSelectedFile(null); }} disabled={uploading}>
            Cancel
          </Button>
          <Button
            onClick={handleUploadDependencies}
            variant="contained"
            color="primary"
            disabled={!selectedFile || uploading}
          >
            {uploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CommonLibrariesManagement;

